// ─────────────────────────────────────────────
// KLOTHIQ — Plan Form Modal (Create / Edit Plan)
// ─────────────────────────────────────────────

import { Input, Textarea } from '@/components/ui/FormControls';
import DatePicker from '@/components/common/DatePicker';
import { useState, useEffect } from 'react';
import { X, Calendar, Flag, Target, AlignLeft } from 'lucide-react';
import { useCreatePlan, useUpdatePlan, type PlanWithMetrics } from '@/hooks/usePlans';
import { workBusinessToday } from '@klothiq/domain';
import { workPlanSchema } from '@klothiq/validation';
import { useWorkIntent } from '@/hooks/useWorkIntent';
import WorkModalShell from '@/components/common/WorkModalShell';

interface PlanFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  planToEdit?:
    | PlanWithMetrics
    | {
        id: string;
        version: number;
        title: string;
        description: string | null;
        goal: string | null;
        start_date: string | null;
        target_date: string | null;
        priority: string;
      }
    | null;
}

export default function PlanFormModal({ isOpen, onClose, planToEdit }: PlanFormModalProps) {
  const isEditing = !!planToEdit;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [goal, setGoal] = useState('');
  const [startDate, setStartDate] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [priority, setPriority] = useState<'low' | 'normal' | 'high' | 'urgent'>('normal');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [baseVersion, setBaseVersion] = useState(1);
  const [baseline, setBaseline] = useState('');
  const intent = useWorkIntent();

  const createPlan = useCreatePlan();
  const updatePlan = useUpdatePlan();

  useEffect(() => {
    intent.reset();
    setBaseVersion(planToEdit?.version ?? 1);
    if (planToEdit) {
      setTitle(planToEdit.title || '');
      setDescription(planToEdit.description || '');
      setGoal(planToEdit.goal || '');
      setStartDate(planToEdit.start_date || '');
      setTargetDate(planToEdit.target_date || '');
      setPriority((planToEdit.priority as any) || 'normal');
    } else {
      setTitle('');
      setDescription('');
      setGoal('');
      setStartDate(workBusinessToday());
      setTargetDate('');
      setPriority('normal');
    }
    setErrorMsg(null);
    setBaseline(
      JSON.stringify([
        planToEdit?.title ?? '',
        planToEdit?.description ?? '',
        planToEdit?.goal ?? '',
        planToEdit?.start_date ?? (planToEdit ? '' : workBusinessToday()),
        planToEdit?.target_date ?? '',
        planToEdit?.priority ?? 'normal',
      ]),
    );
  }, [planToEdit?.id, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (createPlan.isPending || updatePlan.isPending) return;
    setErrorMsg(null);

    const trimmedTitle = title.trim();
    if (trimmedTitle.length < 3 || trimmedTitle.length > 120) {
      setErrorMsg('Plan title must be between 3 and 120 characters.');
      return;
    }

    if (startDate && targetDate && targetDate < startDate) {
      setErrorMsg('Target completion date cannot be earlier than start date.');
      return;
    }

    try {
      const fields = workPlanSchema.parse({
        title: trimmedTitle,
        description: description.trim() || null,
        goal: goal.trim() || null,
        start_date: startDate || null,
        target_date: targetDate || null,
        priority,
      });
      if (isEditing && planToEdit) {
        const input = {
          plan_id: planToEdit.id,
          expected_version: baseVersion,
          ...fields,
        };
        await updatePlan.mutateAsync({ ...input, request_key: intent.key('update_plan', input) });
      } else {
        await createPlan.mutateAsync({ ...fields, request_key: intent.key('create_plan', fields) });
      }
      intent.reset();
      onClose();
    } catch (err: any) {
      setErrorMsg(
        err.issues?.[0]?.message ?? err.message ?? 'An error occurred while saving the plan.',
      );
    }
  };

  const isPending = createPlan.isPending || updatePlan.isPending;
  const isDirty =
    baseline !== JSON.stringify([title, description, goal, startDate, targetDate, priority]);
  const requestClose = () => {
    if (!isPending && (!isDirty || window.confirm('Discard your unsaved plan changes?'))) onClose();
  };

  return (
    <WorkModalShell
      open={isOpen}
      onClose={requestClose}
      busy={isPending}
      title={isEditing ? 'Edit plan' : 'Create plan'}
    >
      <div className="flex items-center justify-between border-b border-border pb-4">
        <div>
          <h2 className="text-lg font-bold text-foreground">
            {isEditing ? 'Edit Plan' : 'Create New Plan'}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Set high-level milestones, goals, and target dates.
          </p>
        </div>
        <button
          onClick={requestClose}
          disabled={isPending}
          aria-label="Close plan form"
          className="min-h-11 min-w-11 rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {errorMsg && (
        <div
          role="alert"
          className="mt-4 rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive"
        >
          {errorMsg}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-4 flex min-h-0 flex-1 flex-col">
        <fieldset
          disabled={isPending}
          className="min-h-0 min-w-0 flex-1 space-y-4 overflow-y-auto pr-1"
        >
          <div>
            <label
              htmlFor="plan-title"
              className="block text-xs font-semibold text-foreground mb-1"
            >
              Plan Title <span className="text-destructive">*</span>
            </label>
            <Input
              id="plan-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Summer Collection Launch 2026"
              maxLength={120}
              required
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-base sm:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              helpLabel="Plan Title *"
            />
          </div>

          <div>
            <label
              htmlFor="plan-goal"
              className="block text-xs font-semibold text-foreground mb-1 flex items-center gap-1.5"
            >
              <Target className="h-3.5 w-3.5 text-primary" /> Key Goal or Objective
            </label>
            <Input
              id="plan-goal"
              type="text"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder="e.g. Launch 40 new SKUs and generate ₹5L sales"
              maxLength={500}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-base sm:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              helpLabel="Key Goal or Objective"
            />
          </div>

          <div>
            <label
              htmlFor="plan-description"
              className="block text-xs font-semibold text-foreground mb-1 flex items-center gap-1.5"
            >
              <AlignLeft className="h-3.5 w-3.5 text-muted-foreground" /> Description & Scope
            </label>
            <Textarea
              id="plan-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Outline plan scope, responsibilities, and target milestones..."
              rows={3}
              maxLength={2000}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-base sm:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              helpLabel="Description & Scope"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="plan-start-date"
                className="block text-xs font-semibold text-foreground mb-1 flex items-center gap-1.5"
              >
                <Calendar className="h-3.5 w-3.5 text-muted-foreground" /> Start Date
              </label>
              <DatePicker
                id="plan-start-date"
                disabled={isPending}
                aria-label="Start date"
                value={startDate}
                onChange={(nextDate) => setStartDate(nextDate ?? '')}
              />
            </div>

            <div>
              <label
                htmlFor="plan-target-date"
                className="block text-xs font-semibold text-foreground mb-1 flex items-center gap-1.5"
              >
                <Calendar className="h-3.5 w-3.5 text-muted-foreground" /> Target Date
              </label>
              <DatePicker
                id="plan-target-date"
                disabled={isPending}
                aria-label="Target date"
                value={targetDate}
                onChange={(nextDate) => setTargetDate(nextDate ?? '')}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1 flex items-center gap-1.5">
              <Flag className="h-3.5 w-3.5 text-muted-foreground" /> Priority
            </label>
            <div className="grid grid-cols-4 gap-2">
              {(['low', 'normal', 'high', 'urgent'] as const).map((p) => {
                const isSelected = priority === p;
                return (
                  <button
                    key={p}
                    aria-pressed={isSelected}
                    type="button"
                    onClick={() => setPriority(p)}
                    className={`min-h-11 rounded-lg py-1.5 text-xs font-semibold capitalize border transition-all ${
                      isSelected
                        ? p === 'urgent'
                          ? 'bg-rose-500/20 border-rose-500 text-rose-500 dark:text-rose-400'
                          : p === 'high'
                            ? 'bg-amber-500/20 border-amber-500 text-amber-500 dark:text-amber-400'
                            : p === 'low'
                              ? 'bg-blue-500/20 border-blue-500 text-blue-500 dark:text-blue-400'
                              : 'bg-primary/20 border-primary text-primary'
                        : 'border-border bg-card text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>
        </fieldset>
        <div className="mt-4 flex shrink-0 items-center justify-end gap-2.5 pt-4 border-t border-border">
          <button
            type="button"
            onClick={requestClose}
            disabled={isPending}
            className="min-h-11 rounded-lg px-4 py-2 text-sm font-semibold text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="min-h-11 rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground shadow hover:bg-primary/90 transition-all disabled:opacity-50"
          >
            {isPending ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Plan'}
          </button>
        </div>
      </form>
    </WorkModalShell>
  );
}
